import {
  PresetAction,
  MessageAction,
  ChoiceAction,
  MultipleChoiceAction,
  TemplateInputAction,
  CallAction,
  ACTION_TYPES,
  ACTOR_TYPES,
  CALL_METHODS,
  SetContextPayload,
  CallActionPayload,
  LocationInputAction,
  SubPresetAction,
} from '../types';

export const createDefaultMessageAction = (): MessageAction => ({
  type: ACTION_TYPES.MESSAGE,
  data: {
    text: 'Enter your message here...',
    actor: ACTOR_TYPES.BOT,
    delay: undefined,
  },
});

export const createDefaultChoiceAction = (): ChoiceAction => ({
  type: ACTION_TYPES.CHOICE,
  data: {
    choiceId: 'choice1',
    choices: {
      option1: {
        text: 'Option 1',
        value: 'option1',
      },
      option2: {
        text: 'Option 2',
        value: 'option2',
      },
    },
  },
});

export const createDefaultMultipleChoiceAction = (): MultipleChoiceAction => ({
  type: ACTION_TYPES.MULTIPLE_CHOICE,
  data: {
    choiceId: 'multiChoice1',
    choices: [
      { text: 'Option 1', value: 'option1' },
      { text: 'Option 2', value: 'option2' },
      { text: 'Option 3', value: 'option3' },
    ],
    minSelections: 1,
    maxSelections: undefined,
    submitButtonText: 'Continue',
  },
});

export const createDefaultTemplateInputAction = (): TemplateInputAction => ({
  type: ACTION_TYPES.TEMPLATE_INPUT,
  data: {
    template: 'I want to {{action,e.g., create a campaign}} for {{cause,e.g., education}}',
  },
});

export const createDefaultLocationInputAction = (): LocationInputAction => ({
  type: ACTION_TYPES.LOCATION_INPUT,
  data: {
    key: 'eventLocation',
  },
});

export const createDefaultSubPresetAction = (): SubPresetAction => ({
  type: ACTION_TYPES.SUB_PRESET,
  data: {
    presetId: '', // This will need to be set by the user
  },
});

export const createDefaultCallAction = (method: string = CALL_METHODS.NAVIGATE): CallAction => {
  const baseAction: CallAction = {
    type: ACTION_TYPES.CALL,
    data: {
      method: method as CallAction['data']['method'],
      payload: {} as CallActionPayload,
      ui: undefined,
    },
  };

  // Set default payloads based on method
  switch (method) {
    case CALL_METHODS.NAVIGATE:
      baseAction.data.payload = { url: '/path/to/page' };
      break;

    case CALL_METHODS.SET_FORM_FIELDS:
      baseAction.data.payload = {
        form: 'app/FormName',
        fields: {},
        streamingDelay: 50,
      };
      break;

    case CALL_METHODS.SET_CONTEXT:
      baseAction.data.payload = {} as SetContextPayload;
      break;

    case CALL_METHODS.BATCH_GENERATE:
      baseAction.data.payload = {
        form: 'app/FormName',
        batches: [],
      };
      baseAction.data.ui = {
        showSpinner: true,
        startMessage: 'Generating content...',
        endMessage: 'Content generated successfully!',
      };
      break;

    case CALL_METHODS.LOAD_NONPROFITS:
      baseAction.data.payload = {
        inputPath: 'selectedNonprofitIds',
        outputPath: 'loadedNonprofits',
        waitForCompletion: true,
      };
      break;

    case CALL_METHODS.LOAD_CAUSES:
      baseAction.data.payload = {
        sdgsOutputPath: 'sdgs',
        categoriesOutputPath: 'categories',
        waitForCompletion: true,
      };
      break;

    case CALL_METHODS.LOAD_PICTURES:
      baseAction.data.payload = {
        outputPath: 'pictures',
        waitForCompletion: true,
      };
      break;

    case CALL_METHODS.LOAD_USER:
      baseAction.data.payload = {
        outputPath: 'user',
        waitForCompletion: true,
      };
      break;

    case CALL_METHODS.SET_DEFAULT_PICTURE:
      baseAction.data.payload = {
        form: 'app/CampaignPageForm',
        categories: [],
      };
      break;

    case CALL_METHODS.DEBUG:
      baseAction.data.payload = {
        message: 'Impact AI Context Debug',
        includeTimestamp: true,
      };
      break;

    case CALL_METHODS.DELAY:
      baseAction.data.payload = { delay: 1000 };
      break;

    case CALL_METHODS.SEARCH_NONPROFITS:
      baseAction.data.payload = {
        outputPath: 'searchResults',
        searchTerm: '',
        limit: 10,
        acceptsDonations: true,
        waitForCompletion: true,
      };
      break;

    case CALL_METHODS.PARSE_DATA:
      baseAction.data.payload = {
        script:
          'function processData(context, ui) {\n  // Your data parsing logic here\n  return context;\n}',
        outputPath: 'parseResult',
        inputPaths: [],
        useFullContext: false,
        timeout: 10000,
      };
      break;

    default:
      return baseAction;
  }

  return baseAction;
};

export const createDefaultAction = (type: string): PresetAction => {
  switch (type) {
    case ACTION_TYPES.MESSAGE:
      return createDefaultMessageAction();

    case ACTION_TYPES.CHOICE:
      return createDefaultChoiceAction();

    case ACTION_TYPES.MULTIPLE_CHOICE:
      return createDefaultMultipleChoiceAction();

    case ACTION_TYPES.TEMPLATE_INPUT:
      return createDefaultTemplateInputAction();

    case ACTION_TYPES.LOCATION_INPUT:
      return createDefaultLocationInputAction();

    case ACTION_TYPES.CALL:
      return createDefaultCallAction();

    case ACTION_TYPES.SUB_PRESET:
      return createDefaultSubPresetAction();

    default:
      return createDefaultMessageAction();
  }
};

export const getActionTypeLabel = (type: string): string => {
  switch (type) {
    case ACTION_TYPES.MESSAGE:
      return 'Message';

    case ACTION_TYPES.CHOICE:
      return 'Single Choice';

    case ACTION_TYPES.MULTIPLE_CHOICE:
      return 'Multiple Choice';

    case ACTION_TYPES.TEMPLATE_INPUT:
      return 'Template Input';

    case ACTION_TYPES.LOCATION_INPUT:
      return 'Location Input';

    case ACTION_TYPES.CALL:
      return 'Method Call';

    case ACTION_TYPES.SUB_PRESET:
      return 'Sub Preset';

    default:
      return 'Unknown';
  }
};

export const getMethodLabel = (method: string): string => {
  switch (method) {
    case CALL_METHODS.NAVIGATE:
      return 'Navigate';

    case CALL_METHODS.SET_FORM_FIELDS:
      return 'Set Form Fields';

    case CALL_METHODS.SET_CONTEXT:
      return 'Set Context';

    case CALL_METHODS.BATCH_GENERATE:
      return 'Batch Generate';

    case CALL_METHODS.LOAD_NONPROFITS:
      return 'Load Nonprofits';

    case CALL_METHODS.LOAD_CAUSES:
      return 'Load Causes';

    case CALL_METHODS.LOAD_PICTURES:
      return 'Load Pictures';

    case CALL_METHODS.LOAD_USER:
      return 'Load User';

    case CALL_METHODS.SET_DEFAULT_PICTURE:
      return 'Set Default Picture';

    case CALL_METHODS.DEBUG:
      return 'Debug';

    case CALL_METHODS.DELAY:
      return 'Delay';

    case CALL_METHODS.SEARCH_NONPROFITS:
      return 'Search Nonprofits';

    case CALL_METHODS.PARSE_DATA:
      return 'Parse Data';

    default:
      return method;
  }
};
